"use client";

import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { graphql } from "gql.tada";
import { Loader2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import { useClient } from "urql";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  type ChatOrderDirection as OrderDirection,
  type ChatOrderKey as OrderKey,
  useAiChatUiContext,
} from "@/lib/ai/ai-chat-ui-context";
import { AgentChatOrder } from "@/lib/ai/enum.types";
import {
  type ChatDateSection,
  getChatDateSection,
  getSectionLabel,
} from "@/lib/ai/format-chat-date";
import { ChatListControls } from "./chat-list-controls";
import { ChatListItem, chatListItemFragment } from "./chat-list-item";
import { NewChatButton } from "./new-chat-button";

const PAGE_SIZE = 30;

const ChatListContentQuery = graphql(
  `query ChatListContent(
    $workspaceId: ID!
    $includesShared: Boolean!
    $orderBy: AgentChatOrder!
    $first: Int!
    $after: ID
  ) {
    viewer {
      id
    }
    agentChats(
      organizationID: $workspaceId
      includesShared: $includesShared
      orderBy: $orderBy
      first: $first
      after: $after
    ) {
      totalCount
      edges {
        node {
          id
          title
          createdAt
          updatedAt
          ...ChatListItem
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }`,
  [chatListItemFragment],
);

type Props = {
  headerClassName?: string;
  onClose?: () => void;
};

/**
 * チャット履歴一覧
 */
export function ChatList({ headerClassName, onClose }: Props) {
  const {
    workspaceId,
    includesShared,
    orderKey,
    orderDirection,
    chatId: selectedChatId,
    toggleOrderDirection,
    setOrder,
    setIncludesShared,
    clearChatSelection,
    lang,
  } = useAiChatUiContext();

  const dateField = orderKey === "CREATED_AT" ? "createdAt" : "updatedAt";

  // 1ページあたりの件数
  const client = useClient();
  const [loadingMore, setLoadingMore] = useState(false);

  const [{ data, error, fetching }, reexecuteQuery] = useQuery({
    query: ChatListContentQuery,
    variables: {
      workspaceId,
      includesShared,
      orderBy: resolveOrderBy(orderKey, orderDirection),
      first: PAGE_SIZE,
      after: null,
    },
    pause: !workspaceId,
  });

  const handleChatDeleted = (deletedChatId: string) => {
    if (selectedChatId && deletedChatId === selectedChatId) {
      clearChatSelection();
    }
    reexecuteQuery({ requestPolicy: "network-only" });
  };

  const handleChatRenamed = () => {
    reexecuteQuery({ requestPolicy: "network-only" });
  };

  const hasNextPage = data?.agentChats.pageInfo.hasNextPage ?? false;
  const endCursor = data?.agentChats.pageInfo.endCursor ?? null;

  const edges = data?.agentChats.edges ?? [];
  const groupedEdges = useMemo(() => {
    const now = new Date();
    const sectionOrder: ChatDateSection[] =
      orderDirection === "ASC"
        ? ["older", "past_month", "past_week", "today"]
        : ["today", "past_week", "past_month", "older"];
    const groups = new Map<ChatDateSection, typeof edges>();
    for (const edge of edges) {
      const dateValue = edge.node[dateField];
      const section = getChatDateSection(dateValue, now);
      const existing = groups.get(section);
      if (existing) {
        existing.push(edge);
      } else {
        groups.set(section, [edge]);
      }
    }
    return sectionOrder
      .filter((s) => groups.has(s))
      .map((s) => ({ section: s, edges: groups.get(s)! }));
  }, [edges, dateField, orderDirection]);

  const handleLoadMore = async () => {
    if (!hasNextPage || !endCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      await client
        .query(
          ChatListContentQuery,
          {
            workspaceId,
            includesShared,
            orderBy: resolveOrderBy(orderKey, orderDirection),
            first: PAGE_SIZE,
            after: endCursor,
          },
          { requestPolicy: "network-only" },
        )
        .toPromise();
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col max-h-full">
      <ChatListControls
        orderKey={orderKey}
        orderDirection={orderDirection}
        includesShared={includesShared}
        onToggleDirection={toggleOrderDirection}
        onOrderKeyChange={(key) => setOrder(key, orderDirection)}
        onIncludesSharedChange={(checked) => setIncludesShared(checked)}
        className={headerClassName}
        onClose={onClose}
      />

      {/* 新規作成ボタンはスクロール範囲の外に配置 */}
      <div className="px-0.5 mt-2">
        <NewChatButton />
      </div>
      <hr className="border-0.5 mt-2" />

      <ScrollArea
        className="flex min-h-0 flex-1"
        viewportClassName="px-0.5 overscroll-contain [&>div]:!block"
      >
        {fetching && !data ? (
          <div className="space-y-1">
            {Array.from({ length: 4 }).map((_, index) => (
              <div
                key={`chat-skeleton-${index}`}
                className={`h-8 animate-pulse rounded-md ${
                  index === 0
                    ? "bg-muted"
                    : index === 1
                      ? "bg-muted/80"
                      : index === 2
                        ? "bg-muted/60"
                        : "bg-muted/40"
                }`}
              />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-md mx-1 border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <Trans>Failed to load chats. Please try again.</Trans>
          </div>
        ) : (
          <>
            <div className="h-2" />
            {groupedEdges.map(({ section, edges: sectionEdges }, index) => (
              <div key={section}>
                {index > 0 && <hr className="mx-3 mt-4 border-border/60" />}
                <div className="px-3 pt-4 pb-1.5 text-xs font-semibold text-muted-foreground">
                  {getSectionLabel(section, lang ?? "ja")}
                </div>
                {sectionEdges.map(({ node }) => (
                  <ChatListItem
                    key={node.id}
                    chat={node}
                    onDeleted={handleChatDeleted}
                    onRenamed={handleChatRenamed}
                    isSelected={selectedChatId === node.id}
                    dateField={dateField}
                  />
                ))}
              </div>
            ))}
            {hasNextPage ? (
              <div className="px-1 py-2">
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full"
                  disabled={loadingMore}
                  onClick={handleLoadMore}
                >
                  {loadingMore ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2Icon className="h-4 w-4 animate-spin" />
                      <Trans>Loading...</Trans>
                    </span>
                  ) : (
                    <Trans
                      id="chat-list.load-more"
                      comment="Pagination button in chat list"
                    >
                      Load more
                    </Trans>
                  )}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </ScrollArea>
    </div>
  );
}

/**
 * 並び替え状態（URLパラメータ）変換ヘルパー
 */
function resolveOrderBy(
  orderKey: OrderKey,
  direction: OrderDirection,
): AgentChatOrder {
  if (orderKey === "CREATED_AT") {
    return direction === "ASC" ? "CREATED_AT_ASC" : "CREATED_AT_DESC";
  }
  return direction === "ASC" ? "UPDATED_AT_ASC" : "UPDATED_AT_DESC";
}
